import {
  Box,
  Grid2,
  Pagination,
  Paper,
  Typography,
  ToggleButton,
  Container,
  ToggleButtonGroup,
  Stack,
  useTheme,
  Button
} from "@mui/material";
import {LayoutGrid, List, Plus} from 'lucide-react';
import {
  FileType,
  PaginatedZyusetuSimplePresenter,
  ZyusetuSimpleFragmentFragment
} from "@/graphql/graphql.ts";
import {Link, useNavigate} from "react-router-dom";
import {Document, Page} from "react-pdf";
import {PdfWorker} from "@/plugin/pdfjs-plugin.ts";
import {Loader2} from "lucide-react";
import {ApprovalStatusChip} from "@/components/pages/Approval/ApprovalStatusChip.tsx";
import {useUserData} from "@/hooks/use-user-data-provider.ts";
import {useMemo, useState} from "react";
import {useConfirmDialog} from "@/hooks/use-confirm-dialog.ts";
import {Permission} from "@/utils/permissions.ts";
import {ZyusetuTemplateSelector, Template} from "@/components/pages/Zyusetu/ZyusetuTemplateSelector.tsx";

interface SimpleZyusetuListViewProps {
  zyusetus?: PaginatedZyusetuSimplePresenter
  getLink?: (zyusetu: ZyusetuSimpleFragmentFragment) => string
  itemsPerPage: number
  handlePageChange: (event: React.ChangeEvent<unknown>, value: number) => void
  page: number
  title: string,
  isLoading?: boolean,
  children?: React.ReactNode
}

export function SimpleZyusetuListView(
  {zyusetus, getLink, itemsPerPage, handlePageChange, page, title, isLoading, children}: SimpleZyusetuListViewProps
) {
  const totalPages = Math.ceil((zyusetus?.total || 0) / itemsPerPage);
  const user = useUserData()
  const navigate = useNavigate();
  const confirmDialog = useConfirmDialog();
  
  const pdfViewerOption = useMemo(() => ({worker: PdfWorker}), [])
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  
  const theme = useTheme()
  
  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'grid' | 'list',
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };
  
  const hasWritePermission = useMemo(() => user.permissions.includes(Permission.WriteZyusetu), [user.permissions]);
  
  const handleOpenNewZyusetuDialog = async () => {
    await confirmDialog.open({
      title: "新規重説を作成",
      fullWidth: true,
      maxWidth: "lg",
      titleSx: {
        py: 2,
        px: 3,
        color: 'common.white',
        fontSize: '1.25rem',
        fontWeight: 'bold'
      },
      children: (
        <Box>
          <Typography variant="subtitle1" sx={{pt: 3, fontWeight: 'bold', textAlign: 'center'}}>
            作成する重説のタイプを選択してください
          </Typography>
          <Container maxWidth="lg" sx={{p: 4}}>
            
            <ZyusetuTemplateSelector
              onSelect={(template: Template) => {
                confirmDialog.close();
                if (hasWritePermission) {
                  navigate(template.path);
                }
              }}
            />
          </Container>
        </Box>
      )
    });
  };
  
  return (
    <Box>
      <Box>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
          <Box sx={{flex: 1}}>
            {title && (
              <Typography variant="pageTitle" sx={{mb: 1}}>
                {title}
              </Typography>
            )}
          </Box>
          <Stack alignItems={'center'}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="表示方法"
              size="small"
            >
              <ToggleButton value="grid" aria-label="グリッド表示">
                <LayoutGrid size={20}/>
              </ToggleButton>
              <ToggleButton value="list" aria-label="リスト表示">
                <List size={20}/>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>
      </Box>
      {children}
      
      {isLoading ? (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "300px"
        }}>
          <Loader2 className="animate-spin" size={40}/>
        </Box>
      ) : zyusetus?.items.length === 0 ? (
        <Grid2 size={12}>
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, my: 5}}>
            <Typography variant="body1" sx={{textAlign: "center"}}>表示する重説がありません</Typography>
            {hasWritePermission &&
              <Button
                variant="contained"
                color="primary"
                startIcon={<Plus size={18}/>}
                onClick={handleOpenNewZyusetuDialog}
              >
                新規作成する
              </Button>
            }
          </Box>
        </Grid2>
      ) : viewMode === 'grid' ? (
        <Grid2 container spacing={2}>
          {zyusetus?.items?.map((zyusetu) => {
            const zumenFiles = zyusetu.files.filter(
              (file) => file.fileType === FileType.Zumen
            );
            
            return (
              <Grid2 size={4} key={`recent-${zyusetu.id}`}>
                <Paper
                  component={Link}
                  to={getLink ? getLink(zyusetu) : `/zyusetu/${zyusetu.id}/edit`}
                  sx={{
                    p: 0,
                    textDecoration: "none",
                    color: "inherit",
                    transition: "all 0.2s",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "background.paper",
                    "&:hover": {
                      boxShadow: 2,
                      transform: 'translateY(-2px)',
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {zumenFiles.length > 0 && (
                    <Box sx={{
                      height: 200,
                      overflow: "hidden",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "60px",
                        background: "linear-gradient(transparent, rgba(0,0,0,0.4))",
                      }
                    }}>
                      {zumenFiles[0].mimeType === "application/pdf" ? (
                        <Document
                          options={pdfViewerOption}
                          file={zumenFiles[0].url}
                          loading={
                            <Box sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%"
                            }}>
                              <Loader2 className="animate-spin" size={24}/>
                            </Box>
                          }
                          error={
                            <Box sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                              color: "error.main"
                            }}>
                              PDFの読み込みに失敗しました
                            </Box>
                          }
                        >
                          <Box sx={{
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "& .react-pdf__Page": {
                              width: "100% !important",
                              height: "100% !important",
                              "& canvas": {
                                objectFit: "contain",
                                width: "100% !important",
                                height: "100% !important",
                              }
                            }
                          }}>
                            <Page
                              pageNumber={1}
                              width={400}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </Box>
                        </Document>
                      ) : (
                        <img
                          src={zumenFiles[0].url ?? undefined}
                          alt="図面プレビュー"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: 4,
                            pointerEvents: "none",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Box sx={{p: 2}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                      <ApprovalStatusChip status={zyusetu.approvalStatus}/>
                      <Typography variant="caption" color="text.secondary">
                        最終更新: {new Date(zyusetu.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{mb: 1, fontWeight: 'bold'}}>{zyusetu.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                      {zyusetu.templateTitle}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                      <Stack direction="row" spacing={2}>
                        {zyusetu.userId !== user?.user?.id && (
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Typography variant="body2" color="text.secondary">作成者：</Typography>
                            <Typography variant="body2">{zyusetu.user?.name}</Typography>
                          </Box>
                        )}
                        {zyusetu.approvalStepExecution && (
                          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Typography variant="body2" color="text.secondary">承認待ち：</Typography>
                            <Typography variant="body2">{zyusetu.approvalStepExecution.user.name}</Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Paper>
              </Grid2>
            );
          })}
        </Grid2>
      ) : (
        <Stack spacing={1}>
          {zyusetus?.items?.map((zyusetu) => {
            const zumenFiles = zyusetu.files.filter(
              (file) => file.fileType === FileType.Zumen
            );
            
            return (
              <Paper
                key={`recent-${zyusetu.id}`}
                component={Link}
                to={getLink ? getLink(zyusetu) : `/zyusetu/${zyusetu.id}/edit`}
                sx={{
                  p: 1,
                  textDecoration: "none",
                  display: "flex",
                  gap: 3,
                  borderRadius: 2,
                  backgroundColor: "background.paper",
                  overflow: "hidden",
                  border: '2px solid transparent',
                  
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: 2
                  },
                }}
              >
                {zumenFiles.length > 0 && (
                  <Box sx={{
                    width: 200,
                    height: 100, overflow: "hidden", flexShrink: 0
                  }}>
                    {zumenFiles[0].mimeType === "application/pdf" ? (
                      <Document
                        options={pdfViewerOption}
                        file={zumenFiles[0].url}
                        loading={
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%"
                          }}>
                            <Loader2 className="animate-spin" size={24}/>
                          </Box>
                        }
                        error={
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: "error.main"
                          }}>
                            PDFの読み込みに失敗しました
                          </Box>
                        }
                      >
                        <Box sx={{
                          width: "100%",
                          height: "100%",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "& .react-pdf__Page": {
                            width: "100% !important",
                            height: "100% !important",
                            "& canvas": {
                              objectFit: "contain",
                              width: "100% !important",
                              height: "100% !important",
                            }
                          }
                        }}>
                          <Page
                            pageNumber={1}
                            width={200}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        </Box>
                      </Document>
                    ) : (
                      <img
                        src={zumenFiles[0].url ?? undefined}
                        alt="図面プレビュー"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: 4,
                          pointerEvents: "none",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                  </Box>
                )}
                <Box sx={{flex: 1}}>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <Stack direction={'row'} gap={3}>
                        <ApprovalStatusChip status={zyusetu.approvalStatus}/>
                        <Typography variant="h6" sx={{fontWeight: 'bold'}}>{zyusetu.title}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        最終更新: {new Date(zyusetu.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {zyusetu.templateTitle}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 3}}>
                      <Stack direction="row" spacing={2}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                          <Typography variant="body2" color="text.secondary">作成者：</Typography>
                          <Typography variant="body2">{zyusetu.user?.name}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}
      
      {!isLoading && totalPages > 1 && (
        <Box sx={{my: 4, display: 'flex', justifyContent: 'center'}}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Box>
  );
}
